import { HardHat } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT, TRANSITION } from '../motion.js';

/** As quatro cantoneiras do enquadramento, cada uma com os dois lados que a formam. */
const CANTOS = [
  'top-0 left-0 border-t-[1.5px] border-l-[1.5px]',
  'top-0 right-0 border-t-[1.5px] border-r-[1.5px]',
  'bottom-0 left-0 border-b-[1.5px] border-l-[1.5px]',
  'bottom-0 right-0 border-b-[1.5px] border-r-[1.5px]',
] as const;

/**
 * O visor da foto que ainda não chegou (design.md §8).
 *
 * Um enquadramento de câmera sobre o rebaixo: quatro cantoneiras e, enquanto
 * carrega, as cantoneiras "procuram o foco" — fecham e abrem devagar — e uma
 * faixa clara varre de baixo para cima, a mesma direção da faixa do botão.
 * Parado, sem foto nenhuma, fica o enquadramento com o capacete no meio.
 *
 * Com movimento reduzido o `MotionConfig` desliga os transforms: sobra só a
 * pulsação de opacidade das cantoneiras, que já diz "esperando".
 */
export function CoverPlaceholder({ busy }: { busy: boolean }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-sunken" aria-hidden>
      {busy ? (
        <motion.span
          className="absolute inset-x-0 top-0 h-1/3 bg-linear-to-t from-transparent via-surface/55 to-transparent"
          initial={{ y: '300%' }}
          animate={{ y: '-100%' }}
          transition={{ duration: 1.5, ease: 'easeInOut', repeat: Infinity, repeatDelay: 0.25 }}
        />
      ) : null}

      <motion.div
        className="absolute inset-3"
        animate={
          busy ? { scale: [1, 0.92, 1], opacity: [0.55, 1, 0.55] } : { scale: 1, opacity: 0.7 }
        }
        transition={busy ? { duration: 1.6, ease: 'easeInOut', repeat: Infinity } : TRANSITION.base}
      >
        {CANTOS.map((canto) => (
          <span key={canto} className={cn('absolute size-3 border-ink-3/60', canto)} />
        ))}
      </motion.div>

      {busy ? null : (
        <span className="absolute inset-0 grid place-items-center text-ink-3">
          <HardHat size={22} strokeWidth={1.5} />
        </span>
      )}
    </div>
  );
}

type CoverState = 'loading' | 'loaded' | 'failed';

/**
 * Foto de capa que se revela ao chegar.
 *
 * Enquanto o arquivo baixa, o visor ocupa o lugar; carregada, a foto entra
 * saindo do desfoque e de um zoom de 4%, em 500ms, e o visor se apaga por
 * baixo dela. Foto que falha fica no visor parado, como obra sem imagem — um
 * ícone de imagem quebrada não diz nada a quem está procurando a obra.
 *
 * Com `sheenOnHover`, a foto já carregada ganha um reflexo sob o ponteiro do
 * `group` em volta, no lugar do zoom: uma faixa de luz a 18%, inclinada, que
 * atravessa a foto uma vez na diagonal, da esquerda para a direita, em 900ms, e
 * some. Não escurece, não cobre e não fica — a foto continua inteira à vista
 * (keyframes `reflexo`, theme.css).
 */
export function CoverImage({
  src,
  alt = '',
  sheenOnHover = false,
  className,
}: {
  src: string;
  alt?: string | undefined;
  sheenOnHover?: boolean | undefined;
  className?: string | undefined;
}) {
  const [state, setState] = useState<CoverState>('loading');
  const [shownSrc, setShownSrc] = useState(src);

  // Outra foto, outro carregamento — ajustado na renderização, não num efeito.
  if (shownSrc !== src) {
    setShownSrc(src);
    setState('loading');
  }

  return (
    <div className={cn('absolute inset-0 overflow-hidden bg-sunken', className)}>
      <AnimatePresence initial={false}>
        {state === 'loaded' ? null : (
          <motion.div
            key="visor"
            className="absolute inset-0"
            exit={{ opacity: 0, transition: { ...TRANSITION.base, delay: 0.2 } }}
          >
            <CoverPlaceholder busy={state === 'loading'} />
          </motion.div>
        )}
      </AnimatePresence>

      {state === 'failed' ? null : (
        <motion.img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
          initial={false}
          animate={
            state === 'loaded'
              ? { opacity: 1, scale: 1, filter: 'blur(0px)' }
              : { opacity: 0, scale: 1.04, filter: 'blur(8px)' }
          }
          transition={{ duration: 0.5, ease: EASE_OUT_QUINT }}
          className="absolute inset-0 size-full object-cover"
        />
      )}

      {sheenOnHover && state === 'loaded' ? (
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-y-1/4 left-0 w-2/5 bg-linear-to-r from-transparent via-white/18 to-transparent opacity-0 group-hover:animate-[reflexo_900ms_ease-in-out_1] group-focus-visible:animate-[reflexo_900ms_ease-in-out_1]"
        />
      ) : null}
    </div>
  );
}
